import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, SignOut, ArrowRight, Clock, CheckCircle, Airplane, MapPin, 
  House, FileText, DownloadSimple, CreditCard, Eye, Plus, Share, WhatsappLogo
} from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import axios from 'axios';
import { usePortalLang, setPortalLang } from '../hooks/usePortalLang';
import { PortalLanguageToggle } from '../components/PortalLanguageToggle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_FLOW = [
  { key: 'intake_miami', label_es: 'Recibido Miami', label_en: 'Intake Miami', icon: House, color: '#2196F3' },
  { key: 'in_transit', label_es: 'En Transito', label_en: 'In Transit', icon: Airplane, color: '#FF9800' },
  { key: 'arrived_san_jose', label_es: 'Llego San Jose', label_en: 'Arrived San Jose', icon: MapPin, color: '#0077FF' },
  { key: 'ready_for_pickup', label_es: 'Listo para Recoger', label_en: 'Ready for Pickup', icon: CheckCircle, color: '#4CAF50' },
  { key: 'delivered', label_es: 'Entregado', label_en: 'Delivered', icon: CheckCircle, color: '#8BC34A' },
];

const getStatusIndex = (s) => STATUS_FLOW.findIndex(f => f.key === s);

export default function PortalDashboardPage() {
  const navigate = useNavigate();
  const { lang, t } = usePortalLang();
  const [summary, setSummary] = useState(null);
  const [packages, setPackages] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('packages');
  const [selectedPkg, setSelectedPkg] = useState(null);

  const customer = JSON.parse(localStorage.getItem('portal_customer') || '{}');
  const token = localStorage.getItem('portal_token');

  // Inline "Add Tracking" submission state — supports pasting multiple tracking numbers (one per line)
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [trackingNumbersText, setTrackingNumbersText] = useState('');
  // Pay-now modal
  const [showPayModal, setShowPayModal] = useState(false);
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [confirmingPayment, setConfirmingPayment] = useState(false);

  const portalApi = axios.create({
    baseURL: `${API_URL}/api`,
    headers: { Authorization: `Bearer ${token}` }
  });

  useEffect(() => {
    if (!token) { navigate('/portal'); return; }
    fetchAll();
    // Auto-refresh every 20s so admin status changes propagate to the client portal
    // without a manual reload. Also refresh when the tab regains focus.
    const interval = setInterval(() => { fetchAll({ silent: true }); }, 20000);
    const onFocus = () => fetchAll({ silent: true });
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async ({ silent = false } = {}) => {
    try {
      const [sumRes, pkgRes, invRes] = await Promise.all([
        portalApi.get('/portal/summary'),
        portalApi.get('/portal/packages'),
        portalApi.get('/portal/invoices')
      ]);
      setSummary(sumRes.data);
      setPackages(pkgRes.data);
      setInvoices(invRes.data);
      // Apply the customer's server-side preferred language only on first load
      // (if a different choice isn't already saved on this device).
      const serverLang = sumRes.data?.customer?.preferred_language;
      if (serverLang && !localStorage.getItem('portal_lang')) {
        setPortalLang(serverLang);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('portal_token');
        localStorage.removeItem('portal_customer');
        navigate('/portal');
      }
      if (!silent) toast.error(t('Error loading data', 'Error al cargar datos'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitTracking = async (e) => {
    e.preventDefault();
    const trackingId = summary?.customer?.tracking_link_id;
    if (!trackingId) {
      toast.error(t('Submit link unavailable', 'Enlace no disponible'));
      return;
    }
    // Parse: one tracking number per line, dedupe, strip empties
    const numbers = [...new Set(
      trackingNumbersText
        .split(/[\n,]+/)
        .map(s => s.trim())
        .filter(Boolean)
    )];
    if (numbers.length === 0) {
      toast.error(t('Paste at least one tracking number', 'Pega al menos un número de tracking'));
      return;
    }
    setSubmitting(true);
    let okCount = 0;
    const failures = [];
    for (const tn of numbers) {
      try {
        await axios.post(`${API_URL}/api/portal/submit/${trackingId}`, { tracking_number: tn });
        okCount += 1;
      } catch (err) {
        failures.push({ tn, reason: err.response?.data?.detail || 'failed' });
      }
    }
    setSubmitting(false);
    if (okCount > 0) {
      toast.success(
        okCount === 1
          ? t(`Tracking ${numbers[0]} submitted!`, `¡Tracking ${numbers[0]} enviado!`)
          : t(`${okCount} tracking numbers submitted!`, `¡${okCount} números enviados!`)
      );
    }
    if (failures.length > 0) {
      const first = failures[0];
      toast.error(
        failures.length === 1
          ? `${first.tn}: ${first.reason}`
          : t(`${failures.length} failed — first: ${first.tn} (${first.reason})`, `${failures.length} fallaron — primero: ${first.tn} (${first.reason})`)
      );
    }
    if (okCount > 0) {
      setShowSubmitModal(false);
      setTrackingNumbersText('');
      fetchAll();
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_customer');
    navigate('/portal');
  };

  const statusLabel = (s) => {
    const item = STATUS_FLOW.find(f => f.key === s);
    if (!item) return s;
    return lang === 'es' ? item.label_es : item.label_en;
  };
  const statusColor = (s) => STATUS_FLOW.find(f => f.key === s)?.color || '#999';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-10 h-10 border-4 border-[#0077FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="portal-dashboard">
      {/* Header */}
      <div className="ship-gradient text-white">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/assets/trackmypackage-logo.png" alt="Track My Package" className="h-20" />
            <div>
              <p className="text-[10px] text-white/60">{t('Customer Portal', 'Portal de Cliente')}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <PortalLanguageToggle variant="dark" />
            <span className="text-sm text-white/80 hidden sm:block">{customer.name}</span>
            <button onClick={handleLogout} className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-testid="portal-logout">
              <SignOut size={20} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Welcome + Add Tracking CTA */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3"
        >
          <div>
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Bebas Neue' }}>
              {t('Hi', 'Hola')}, {customer.name?.split(' ')[0]}
            </h2>
            <p className="text-sm text-gray-500">
              {t('View your packages and invoices', 'Aquí puedes ver tus paquetes y facturas')}
            </p>
          </div>
          <Button
            onClick={() => setShowSubmitModal(true)}
            className="ship-gradient text-white rounded-lg self-start sm:self-auto"
            data-testid="portal-add-tracking-btn"
          >
            <Plus size={18} className="mr-1.5" weight="bold" />
            {t('Add Tracking', 'Agregar Tracking')}
          </Button>
        </motion.div>

        {/* Miami Warehouse Address — Ship-To label for the client */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.03 }}
          className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-orange-50 to-white p-5 shadow-sm"
          data-testid="miami-warehouse-address-card"
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-amber-500 text-white flex items-center justify-center shadow-md">
              <MapPin size={20} weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-amber-700 font-bold mb-1">
                {t('Ship Your Packages To', 'Envía tus paquetes a')}
              </p>
              <p className="text-sm font-semibold text-gray-900 leading-relaxed" data-testid="miami-warehouse-address-text">
                <span className="text-[#0077FF]">{customer.name || t('Your Name', 'Tu Nombre')}</span> / ShipCR<br />
                6991 NW 82 AV SUITE #06<br />
                MIAMI, FL 33195-2933<br />
                United States
              </p>
              <p className="text-[11px] text-gray-500 mt-2 italic">
                {t(
                  'Use this exact address with your name when ordering online.',
                  'Usa esta dirección exacta con tu nombre al comprar en línea.'
                )}
              </p>
              <Button
                size="sm"
                variant="outline"
                className="mt-3 rounded-lg border-amber-300 text-amber-700 hover:bg-amber-100"
                onClick={() => {
                  const text = `${customer.name || 'Your Name'} / ShipCR\n6991 NW 82 AV SUITE #06\nMIAMI, FL 33195-2933\nUnited States`;
                  navigator.clipboard.writeText(text);
                  toast.success(t('Address copied to clipboard', 'Dirección copiada'));
                }}
                data-testid="copy-miami-address-btn"
              >
                {t('Copy Address', 'Copiar Dirección')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="mt-3 ml-2 rounded-lg border-[#0077FF]/30 text-[#0077FF] hover:bg-[#0077FF]/5"
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API_URL}/api/portal/ship-label.pdf`, {
                      headers: { Authorization: `Bearer ${token}` },
                      responseType: 'blob',
                    });
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `ship-label-${(customer.name || 'client').replace(/\s+/g, '_')}.pdf`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success(t('Ship-To label downloaded!', '¡Etiqueta descargada!'));
                  } catch (err) {
                    toast.error(t('Failed to download label', 'Error al descargar la etiqueta'));
                  }
                }}
                data-testid="download-ship-label-btn"
              >
                <DownloadSimple size={14} weight="bold" className="mr-1" />
                {t('Download PDF Label', 'Descargar Etiqueta PDF')}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Personal Submit Link */}
        {summary?.customer?.tracking_link_id && summary?.customer?.company_id && (() => {
          const PUBLIC_SITE = process.env.REACT_APP_PUBLIC_SITE_URL || 'https://trackmypackage.app';
          const personalLink = `${PUBLIC_SITE}/portal/submit/${summary.customer.tracking_link_id}`;
          const handleCopy = () => {
            navigator.clipboard.writeText(personalLink);
            toast.success(t('Link copied! Save it to submit packages anytime.', '¡Enlace copiado! Guárdalo para enviar paquetes en cualquier momento.'));
          };
          return (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="bg-gradient-to-r from-indigo-50 via-sky-50 to-emerald-50 border border-sky-200/60 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-3"
              data-testid="personal-submit-link-card"
            >
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-wide text-sky-700 font-bold mb-1">
                  {t('Your Personal Submit Link', 'Tu Enlace Personal')}
                </p>
                <p className="text-xs text-gray-500 mb-2 leading-relaxed">
                  {t(
                    'Bookmark this — anyone with this link can submit a tracking number to your account (no login needed).',
                    'Guarda este enlace — cualquier persona con este enlace puede enviar un número de tracking a tu cuenta (sin inicio de sesión).'
                  )}
                </p>
                <code
                  className="text-xs sm:text-sm text-gray-800 bg-white/80 border border-sky-200/50 rounded-md px-2 py-1 inline-block max-w-full truncate"
                  data-testid="personal-submit-link-text"
                >
                  {personalLink}
                </code>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  onClick={handleCopy}
                  size="sm"
                  variant="outline"
                  className="rounded-lg border-sky-300 text-sky-700 hover:bg-sky-100"
                  data-testid="copy-personal-link-btn"
                >
                  {t('Copy Link', 'Copiar Enlace')}
                </Button>
                <Button
                  onClick={() => window.open(personalLink, '_blank')}
                  size="sm"
                  className="rounded-lg ship-gradient text-white"
                  data-testid="open-personal-link-btn"
                >
                  {t('Submit Now', 'Enviar Ahora')}
                  <ArrowRight size={14} className="ml-1" />
                </Button>
              </div>
            </motion.div>
          );
        })()}

        {/* KPI Cards */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { label: t('Packages', 'Paquetes'), value: summary.total_packages, color: 'text-blue-600', bg: 'bg-blue-50' },
              { label: t('Total Cost', 'Costo Total'), value: `$${summary.total_cost_usd.toFixed(2)}`, color: 'text-[#0077FF]', bg: 'bg-teal-50' },
              { label: t('Paid', 'Pagado'), value: `$${summary.total_paid_usd.toFixed(2)}`, color: 'text-green-600', bg: 'bg-green-50' },
              { label: t('Pending', 'Pendiente'), value: `$${summary.balance_usd.toFixed(2)}`, color: 'text-[#FFB300]', bg: 'bg-amber-50' },
            ].map((kpi, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-white rounded-xl p-4 shadow-sm"
              >
                <p className="text-[10px] uppercase tracking-wide text-gray-400">{kpi.label}</p>
                <p className={`text-xl font-bold mt-1 ${kpi.color}`}>{kpi.value}</p>
              </motion.div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-xl p-1 shadow-sm">
          {['packages', 'invoices'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab 
                  ? 'ship-gradient text-white shadow-md' 
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
              data-testid={`portal-tab-${tab}`}
            >
              {tab === 'packages' ? (
                <><Package size={16} className="inline mr-1.5" /> {t('Packages', 'Paquetes')} ({packages.length})</>
              ) : (
                <><FileText size={16} className="inline mr-1.5" /> {t('Invoices', 'Facturas')} ({invoices.length})</>
              )}
            </button>
          ))}
        </div>

        {/* Packages Tab */}
        {activeTab === 'packages' && (
          <div className="space-y-3">
            {packages.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center shadow-sm">
                <Package size={48} className="mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">{t('No packages yet', 'No tienes paquetes')}</p>
              </div>
            ) : packages.map((pkg, i) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="bg-white rounded-xl shadow-sm overflow-hidden"
              >
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-mono text-xs text-gray-400">{pkg.tracking_number}</p>
                      <p className="font-medium mt-0.5">{pkg.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-[#0077FF]">${pkg.shipping_cost?.toFixed(2)}</p>
                      <p className="text-[10px] text-gray-400">{pkg.weight_lbs} lbs</p>
                    </div>
                  </div>

                  {/* Status Timeline */}
                  <div className="flex items-center gap-1">
                    {STATUS_FLOW.map((step, si) => {
                      const currentIdx = getStatusIndex(pkg.status);
                      const isDone = si <= currentIdx;
                      const isCurrent = si === currentIdx;
                      return (
                        <div key={step.key} className="flex items-center flex-1">
                          <div className={`flex flex-col items-center flex-1 ${si > 0 ? '' : ''}`}>
                            {si > 0 && (
                              <div className={`w-full h-0.5 mb-1 rounded ${isDone ? 'bg-[#0077FF]' : 'bg-gray-200'}`} />
                            )}
                            <div 
                              className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                                isCurrent ? 'ring-2 ring-offset-1' : ''
                              }`}
                              style={{ 
                                backgroundColor: isDone ? step.color : '#e5e7eb',
                                ringColor: isCurrent ? step.color : 'transparent'
                              }}
                            >
                              <step.icon size={12} className={isDone ? 'text-white' : 'text-gray-400'} weight={isDone ? 'fill' : 'regular'} />
                            </div>
                            <p className={`text-[8px] mt-1 text-center leading-tight ${isCurrent ? 'font-bold text-gray-700' : 'text-gray-400'}`}>
                              {(lang === 'es' ? step.label_es : step.label_en).split(' ')[0]}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Status bar */}
                <div 
                  className="px-4 py-2 text-xs font-medium text-white text-center"
                  style={{ backgroundColor: statusColor(pkg.status) }}
                >
                  {statusLabel(pkg.status)}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div className="space-y-3">
            {invoices.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center shadow-sm">
                <FileText size={48} className="mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">{t('No invoices yet', 'No tienes facturas')}</p>
              </div>
            ) : invoices.map((inv, i) => (
              <motion.div
                key={inv.invoice_number}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="bg-white rounded-xl shadow-sm p-4"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono text-sm font-bold">{inv.invoice_number}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {new Date(inv.created_at).toLocaleDateString()}
                      {inv.due_date && ` | ${t('Due', 'Vence')}: ${new Date(inv.due_date).toLocaleDateString()}`}
                    </p>
                    {(inv.package_count > 0 || inv.transport_fee > 0) && (
                      <p className="text-[11px] text-gray-500 mt-1" data-testid={`invoice-breakdown-${inv.invoice_number}`}>
                        {inv.package_count > 0 && (
                          <span>{inv.package_count} {inv.package_count === 1 ? t('package', 'paquete') : t('packages', 'paquetes')}</span>
                        )}
                        {inv.subtotal_usd != null && (
                          <span> · {t('Subtotal', 'Subtotal')} ${Number(inv.subtotal_usd).toFixed(2)}</span>
                        )}
                        {inv.transport_fee > 0 && (
                          <span> + {t('Transport', 'Transporte')} ${Number(inv.transport_fee).toFixed(2)}</span>
                        )}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-[#0077FF]">${inv.total_usd?.toFixed(2)}</p>
                    <p className="text-xs text-[#FFB300] font-medium">₡{inv.total_crc?.toLocaleString()}</p>
                  </div>
                </div>
                
                <div className="flex items-center justify-between mt-3 pt-3 border-t">
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                    inv.status === 'paid' ? 'bg-green-100 text-green-700' :
                    inv.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {inv.status === 'paid' ? t('Paid', 'Pagado') : inv.status === 'pending' ? t('Pending', 'Pendiente') : inv.status}
                  </span>
                  
                  <div className="flex gap-2">
                    <a
                      href={`/invoice/${inv.invoice_number}`}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title={t('View invoice', 'Ver factura')}
                      data-testid={`portal-view-invoice-${inv.invoice_number}`}
                    >
                      <Eye size={18} className="text-gray-500" />
                    </a>
                    <a
                      href={`${API_URL}/api/invoices/public/${inv.invoice_number}/pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 hover:bg-teal-50 rounded-lg transition-colors"
                      title={t('Download PDF', 'Descargar PDF')}
                      data-testid={`portal-download-pdf-${inv.invoice_number}`}
                    >
                      <DownloadSimple size={18} className="text-[#0077FF]" />
                    </a>
                    <button
                      onClick={async () => {
                        const url = `${window.location.origin}/invoice/${inv.invoice_number}`;
                        // Use native share when available (mobile), otherwise copy
                        if (navigator.share) {
                          try {
                            await navigator.share({ title: `Invoice ${inv.invoice_number}`, url });
                            return;
                          } catch { /* user cancelled */ }
                        }
                        try {
                          await navigator.clipboard.writeText(url);
                          toast.success(t('Link copied!', '¡Enlace copiado!'));
                        } catch {
                          toast.error(t('Failed to copy', 'Error al copiar'));
                        }
                      }}
                      className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                      title={t('Copy / share invoice link', 'Copiar / compartir enlace')}
                      data-testid={`portal-share-invoice-${inv.invoice_number}`}
                    >
                      <Share size={18} className="text-[#0077FF]" />
                    </button>
                    <button
                      onClick={() => {
                        const url = `${window.location.origin}/invoice/${inv.invoice_number}`;
                        const msg = lang === 'es'
                          ? `Mi factura ${inv.invoice_number} por $${Number(inv.total_usd || 0).toFixed(2)}: ${url}`
                          : `My invoice ${inv.invoice_number} for $${Number(inv.total_usd || 0).toFixed(2)}: ${url}`;
                        window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
                      }}
                      className="p-2 hover:bg-green-50 rounded-lg transition-colors"
                      title={t('Share via WhatsApp', 'Compartir por WhatsApp')}
                      data-testid={`portal-whatsapp-invoice-${inv.invoice_number}`}
                    >
                      <WhatsappLogo size={18} weight="bold" className="text-green-600" />
                    </button>
                    {inv.status === 'pending' && (
                      <button
                        onClick={() => { setPayingInvoice(inv); setShowPayModal(true); }}
                        className="flex items-center gap-1 px-3 py-1.5 bg-[#FFB300] text-white text-xs font-medium rounded-lg hover:bg-[#FFA000] transition-colors"
                        data-testid={`portal-pay-${inv.invoice_number}`}
                      >
                        <CreditCard size={14} />
                        {t('Pay', 'Pagar')}
                      </button>
                    )}
                    {inv.client_payment_choice && inv.status !== 'paid' && (
                      <span className="ml-1 inline-flex items-center gap-1 text-[10px] text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-0.5">
                        ✓ {(lang === 'es' ? {
                          sinpe: 'Pagaré por SINPE',
                          paypal: 'Pagaré por PayPal',
                          cash_on_delivery: 'Pagaré en efectivo al entregar',
                          card_on_delivery: 'Pagaré con tarjeta al entregar',
                        } : {
                          sinpe: 'Will pay via SINPE',
                          paypal: 'Will pay via PayPal',
                          cash_on_delivery: 'Will pay cash on delivery',
                          card_on_delivery: 'Will pay card on delivery',
                        })[inv.client_payment_choice] || inv.client_payment_choice}
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="text-center py-4">
          <p className="text-xs text-gray-400">
            Track My Package | info@trackmypackage.app
          </p>
        </div>

        {/* Submit Tracking Modal — paste one or more tracking numbers, one per line */}
        <Dialog open={showSubmitModal} onOpenChange={setShowSubmitModal}>
          <DialogContent className="max-w-md" data-testid="submit-tracking-modal">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold tracking-wide" style={{ fontFamily: 'Bebas Neue' }}>
                {t('Submit Tracking', 'Enviar Tracking')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmitTracking} className="space-y-4 mt-2">
              <div>
                <Label className="text-xs uppercase tracking-wide text-gray-500">
                  {t('Tracking Number(s) — paste one per line', 'Número(s) de Tracking — uno por línea')}
                </Label>
                <textarea
                  value={trackingNumbersText}
                  onChange={(e) => setTrackingNumbersText(e.target.value)}
                  placeholder={'1Z999AA10123456784\n9400111899223197428496\nTBA303938499000'}
                  rows={6}
                  className="w-full mt-1 rounded-lg border border-input bg-background px-3 py-2 text-sm font-mono leading-relaxed shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-y"
                  required
                  autoFocus
                  data-testid="submit-tracking-numbers-textarea"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  {t(
                    "Copy directly from Amazon, UPS, FedEx, USPS, etc. We'll add each one to your account.",
                    'Copia directamente de Amazon, UPS, FedEx, USPS, etc. Agregaremos cada uno a tu cuenta.'
                  )}
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowSubmitModal(false)}
                  disabled={submitting}
                  className="rounded-lg"
                  data-testid="submit-tracking-cancel-btn"
                >
                  {t('Cancel', 'Cancelar')}
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="ship-gradient text-white rounded-lg"
                  data-testid="submit-tracking-confirm-btn"
                >
                  {submitting ? t('Submitting...', 'Enviando...') : t('Submit', 'Enviar')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Pay Now modal — client chooses payment method */}
        <Dialog open={showPayModal} onOpenChange={(open) => { setShowPayModal(open); if (!open) setPayingInvoice(null); }}>
          <DialogContent className="max-w-md" data-testid="pay-invoice-modal">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold tracking-wide" style={{ fontFamily: 'Bebas Neue' }}>
                {t('How do you want to pay?', '¿Cómo deseas pagar?')}
              </DialogTitle>
            </DialogHeader>
            {payingInvoice && (
              <div className="space-y-4 mt-2">
                <div className="rounded-xl border-2 border-amber-200 bg-amber-50/40 p-4">
                  <p className="text-[11px] uppercase tracking-wider text-gray-500">{payingInvoice.invoice_number}</p>
                  <div className="flex items-baseline justify-between mt-1">
                    <span className="text-2xl font-bold text-[#0077FF]">${payingInvoice.total_usd?.toFixed(2)} USD</span>
                    <span className="text-lg font-bold text-[#FF7A00]">₡{payingInvoice.total_crc?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} CRC</span>
                  </div>
                  {(payingInvoice.package_count > 0 || payingInvoice.transport_fee > 0) && (
                    <div className="mt-2 pt-2 border-t border-amber-200 text-xs text-gray-600 space-y-0.5" data-testid="pay-modal-breakdown">
                      {payingInvoice.package_count > 0 && (
                        <div className="flex justify-between">
                          <span>{payingInvoice.package_count} {payingInvoice.package_count === 1 ? t('package', 'paquete') : t('packages', 'paquetes')} ({t('subtotal', 'subtotal')})</span>
                          <span className="font-semibold">${Number(payingInvoice.subtotal_usd || 0).toFixed(2)}</span>
                        </div>
                      )}
                      {payingInvoice.transport_fee > 0 && (
                        <div className="flex justify-between">
                          <span>{t('Shipping / Transport', 'Envío / Transporte')}</span>
                          <span className="font-semibold">${Number(payingInvoice.transport_fee).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  )}
                  {payingInvoice.exchange_rate && (
                    <p className="text-[10px] text-gray-400 mt-1">1 USD ≈ ₡{Number(payingInvoice.exchange_rate).toFixed(2)} ({t('live rate', 'tipo de cambio')})</p>
                  )}
                </div>

                {[
                  { id: 'sinpe', label: t('SINPE Móvil', 'SINPE Móvil'), sub: '₡' + (payingInvoice.total_crc?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || ''), color: 'border-emerald-200 hover:bg-emerald-50' },
                  { id: 'paypal', label: 'PayPal', sub: t('Pay in USD', 'Pago en USD'), color: 'border-blue-200 hover:bg-blue-50' },
                  { id: 'cash_on_delivery', label: t('Cash on Delivery', 'Efectivo al entregar'), sub: t('Pay the driver', 'Paga al conductor'), color: 'border-amber-200 hover:bg-amber-50' },
                  { id: 'card_on_delivery', label: t('Card on Delivery', 'Tarjeta al entregar'), sub: t('Credit or debit', 'Crédito o débito'), color: 'border-violet-200 hover:bg-violet-50' },
                ].map(opt => (
                  <button
                    key={opt.id}
                    disabled={confirmingPayment}
                    onClick={async () => {
                      setConfirmingPayment(true);
                      try {
                        await axios.post(`${API_URL}/api/portal/invoices/${payingInvoice.id}/choose-payment?method=${opt.id}`, null, {
                          headers: { Authorization: `Bearer ${token}` },
                        });
                        toast.success(`✓ ${opt.label}`);
                        // For SINPE, keep the modal open and show payment details instead of closing
                        if (opt.id === 'sinpe' && summary?.payment_settings?.sinpe_number) {
                          setPayingInvoice(prev => ({ ...prev, client_payment_choice: 'sinpe' }));
                          fetchAll();
                          setConfirmingPayment(false);
                          return;
                        }
                        setShowPayModal(false);
                        setPayingInvoice(null);
                        fetchAll();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || t('Failed', 'Error'));
                      } finally {
                        setConfirmingPayment(false);
                      }
                    }}
                    className={`w-full rounded-xl border-2 ${opt.color} p-3 text-left transition-all flex items-center justify-between gap-3 disabled:opacity-50`}
                    data-testid={`pay-option-${opt.id}`}
                  >
                    <div>
                      <p className="font-bold text-gray-900">{opt.label}</p>
                      <p className="text-[11px] text-gray-500">{opt.sub}</p>
                    </div>
                    <ArrowRight size={18} className="text-gray-400" />
                  </button>
                ))}

                {/* SINPE details panel shown after client picks SINPE */}
                {payingInvoice.client_payment_choice === 'sinpe' && summary?.payment_settings?.sinpe_number && (
                  <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4" data-testid="sinpe-details-panel">
                    <p className="text-[11px] uppercase tracking-wider text-emerald-700 font-bold mb-2">
                      📱 {t('SINPE Móvil Info', 'Datos SINPE Móvil')}
                    </p>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-gray-600">{t('Number', 'Número')}:</span>
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono font-bold text-emerald-700 text-lg">{summary.payment_settings.sinpe_number}</span>
                          <button
                            onClick={() => { navigator.clipboard.writeText(summary.payment_settings.sinpe_number); toast.success(t('Number copied', 'Número copiado')); }}
                            className="text-xs text-emerald-600 hover:text-emerald-800 underline"
                          >copy</button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-gray-600">{t('Name', 'Nombre')}:</span>
                        <span className="font-bold">{summary.payment_settings.sinpe_name}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-gray-600">{t('Amount', 'Monto')}:</span>
                        <div className="flex items-center gap-1.5">
                          <span className="font-bold text-[#FF7A00] text-base">₡{payingInvoice.total_crc?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          <button
                            onClick={() => { navigator.clipboard.writeText(String(Math.round(payingInvoice.total_crc))); toast.success(t('Amount copied', 'Monto copiado')); }}
                            className="text-xs text-emerald-600 hover:text-emerald-800 underline"
                          >copy</button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-gray-600">{t('Reference', 'Referencia')}:</span>
                        <span className="font-mono font-bold">{payingInvoice.invoice_number}</span>
                      </div>
                    </div>
                    <a
                      href={`sms:2627&body=PASE ${Math.round(payingInvoice.total_crc)} ${summary.payment_settings.sinpe_number.replace(/\D/g, '')} ${payingInvoice.invoice_number}`.replace(/ /g, '%20')}
                      className="mt-3 w-full inline-block text-center bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold py-2 rounded-lg"
                      data-testid="sinpe-sms-link"
                    >
                      {t('Open SINPE Móvil →', 'Abrir SINPE Móvil →')}
                    </a>
                    <p className="text-[10px] text-gray-500 text-center mt-2">
                      {t('Or send an SMS to 2627', 'O envía un SMS a 2627')}: <code className="bg-white px-1 rounded">PASE {Math.round(payingInvoice.total_crc)} {summary.payment_settings.sinpe_number.replace(/\D/g, '')} {payingInvoice.invoice_number}</code>
                    </p>
                  </div>
                )}

                <p className="text-[10px] text-gray-400 text-center mt-2">
                  {t(
                    'We will confirm your selection by email.',
                    'Te confirmaremos por correo.'
                  )}
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
